/**
 * API functions for admin debug pages.
 *
 * Provides functions to interact with the backend API for Return debugging
 * and OneClick session diagnostics.
 */

import axios from "axios";
import { AXIOS_CONFIG } from "@/utils/api";
import { backend_url } from "@/constants";
import { Endpoints } from "@/utils/api/endpoints";
import type {
  ApiResponse,
  ClearAction,
  ClearActionResponse,
  IntegrationLogsData,
  ReturnDebugData,
  TimestepDetailData,
} from "./types";

/**
 * Fetches debug information for a specific return including all timesteps.
 *
 * @param {string} returnId - The UUID of the return
 * @returns {Promise<ReturnDebugData>} A promise that resolves to the return debug data
 * @throws {Error} If the API request fails
 */
export const fetchReturnDebugInfo = async (
  returnId: string
): Promise<ReturnDebugData> => {
  try {
    const endpoint = Endpoints.admin.returnDebug.replace(":returnId", returnId);
    const response = await axios.get<ApiResponse<ReturnDebugData>>(
      `${backend_url}/${endpoint}`,
      {
        ...AXIOS_CONFIG,
      }
    );

    return response.data.content;
  } catch (error) {
    console.error("Failed to load return debug information", error);
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      throw new Error("You do not have permission to access this resource");
    }
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error("Return not found");
    }
    throw new Error("Failed to load return debug information");
  }
};

/**
 * Fetches detailed information for a specific timestep including logs.
 *
 * @param {string} returnId - The UUID of the return
 * @param {string} timestepId - The UUID of the timestep
 * @returns {Promise<TimestepDetailData>} A promise that resolves to the timestep detail data
 * @throws {Error} If the API request fails
 */
export const fetchTimestepDetail = async (
  returnId: string,
  timestepId: string
): Promise<TimestepDetailData> => {
  try {
    const endpoint = Endpoints.admin.timestepDetail
      .replace(":returnId", returnId)
      .replace(":timestepId", timestepId);
    const response = await axios.get<ApiResponse<TimestepDetailData>>(
      `${backend_url}/${endpoint}`,
      {
        ...AXIOS_CONFIG,
      }
    );

    return response.data.content;
  } catch (error) {
    console.error("Failed to load timestep details", error);
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      throw new Error("You do not have permission to access this resource");
    }
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error("Timestep not found");
    }
    throw new Error("Failed to load timestep details");
  }
};

/**
 * Clears data for a specific return (state, cache, logs, or all).
 *
 * @param {string} returnId - The UUID of the return
 * @param {ClearAction} action - The type of data to clear
 * @returns {Promise<string>} A promise that resolves to a success message
 * @throws {Error} If the API request fails
 */
export const clearReturnData = async (
  returnId: string,
  action: ClearAction
): Promise<string> => {
  try {
    const endpointTemplate = getActionEndpoint(action);
    const endpoint = endpointTemplate.replace(":returnId", returnId);
    const response = await axios.post<ApiResponse<ClearActionResponse>>(
      `${backend_url}/${endpoint}`,
      {},
      {
        ...AXIOS_CONFIG,
      }
    );

    return response.data.content.message;
  } catch (error) {
    console.error(`Failed to clear ${action}`, error);
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      throw new Error("You do not have permission to perform this action");
    }
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error("Return not found");
    }
    throw new Error(`Failed to clear ${action}`);
  }
};

/**
 * Helper function to map clear action to API endpoint.
 *
 * @param {ClearAction} action - The type of data to clear
 * @returns {string} The API endpoint path
 */
function getActionEndpoint(action: ClearAction): string {
  const endpoints: Record<ClearAction, string> = {
    state: Endpoints.admin.clearState,
    cache: Endpoints.admin.clearCache,
    logs: Endpoints.admin.clearLogs,
    all: Endpoints.admin.clearAll,
  };
  return endpoints[action];
}

/**
 * Copies tax year and user ID to clipboard for SmartSync import.
 *
 * @param {string} year - Tax year
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const copyTaxYearToClipboard = async (
  year: string,
  userId: string
): Promise<void> => {
  const jsonData = JSON.stringify({ taxYear: year, userId: userId });
  await navigator.clipboard.writeText(jsonData);
};

/**
 * Fetches integration onboarding logs for a specific user.
 *
 * @param {string} userId - The UUID of the user
 * @returns {Promise<IntegrationLogsData>} A promise that resolves to the integration logs data
 * @throws {Error} If the API request fails
 */
export const fetchIntegrationLogs = async (
  userId: string
): Promise<IntegrationLogsData> => {
  try {
    const endpoint = Endpoints.admin.integrationLogs.replace(":userId", userId);
    const response = await axios.get<ApiResponse<IntegrationLogsData>>(
      `${backend_url}/${endpoint}`,
      {
        ...AXIOS_CONFIG,
      }
    );

    return response.data.content;
  } catch (error) {
    console.error("Failed to load integration logs", error);
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      throw new Error("You do not have permission to access this resource");
    }
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error("User not found");
    }
    throw new Error("Failed to load integration logs");
  }
};
